import { GraviGrid } from '@gravitate-js/excalibrr'
import { useBulkOrderUpdate } from '@api/useBulkOrderUpdate'
import { useMemo, useRef } from 'react'

import { useColumnDefs } from './columnDefs'

interface BulkOrderJobsGridProps {
  onJobSelect?: (jobId: string) => void
  selectedJobId?: string | null
}

const STORAGE_KEY = 'bulk-order-jobs-grid'

export function BulkOrderJobsGrid({ onJobSelect, selectedJobId }: BulkOrderJobsGridProps) {
  const gridRef = useRef(null)

  const { useListJobsQuery, cancelJobMutation } = useBulkOrderUpdate()

  const { data, isLoading, error } = useListJobsQuery({
    limit: 100,
    offset: 0,
  })

  const jobsData = data?.jobs || []

  const handleCancelJob = (jobId: string) => {
    cancelJobMutation.mutate(jobId)
  }

  const handleViewDetails = (jobId: string) => {
    onJobSelect?.(jobId)
  }

  const columnDefs = useColumnDefs(handleCancelJob, handleViewDetails)

  const agPropOverrides = useMemo(
    () => ({
      getRowId: (params: any) => params.data.id,
      rowHeight: 50,
      headerHeight: 40,
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false,
      },
      onRowClicked: (event: any) => {
        const jobId = event.data.id
        if (jobId !== selectedJobId) {
          onJobSelect?.(jobId)
        }
      },
      rowSelection: 'single',
      suppressRowGroupHidesColumns: true,
      rowGroupPanelShow: 'never',
      getRowClass: (params: any) => {
        const status = params.data?.status
        const isSelected = selectedJobId === params.data?.id

        let baseClass = ''
        if (status === 'failed' || status === 'cancelled') baseClass = 'row-error'
        else if (status === 'completed') baseClass = 'row-success'
        else if (status === 'running') baseClass = 'row-warning font-medium border-l-4 border-l-orange-400'

        const selectedClass = isSelected ? 'selected-row' : ''

        return `${baseClass} ${selectedClass}`.trim()
      },
      onFirstDataRendered: (params: any) => {
        if (!selectedJobId && params.api.getDisplayedRowCount() > 0) {
          const firstRow = params.api.getDisplayedRowAtIndex(0)
          if (firstRow) {
            firstRow.setSelected(true)
            onJobSelect?.(firstRow.data.id)
          }
        }
      },
    }),
    [selectedJobId, onJobSelect]
  )

  return (
    <div className='h-full' style={{ minHeight: '400px' }}>
      <GraviGrid
        externalRef={gridRef}
        primaryKey='id'
        controlBarProps={null}
        columnDefs={columnDefs}
        rowData={jobsData}
        agPropOverrides={agPropOverrides}
        loading={isLoading}
        error={error}
      />
    </div>
  )
}
